import { CloseRounded } from '@mui/icons-material'
import { Box, Button, DialogContent, DialogTitle, Grid, Tab, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IRootState } from '../../../../../../../redux/types'
import MassAssigmentBody from '../../../../../../shared/components/MassAssigmentBody'
import { StyledTabs } from '../../../../../../shared/components/StyledTabs'
import TabPanel from '../../../../../../shared/components/TabPanel'
import { BrandMultiAutocomplete } from '../../../../../../shared/components/inputs'
import { FamilyAutocomplete } from '../../../../../../shared/components/inputs/FamilyAutocomplete'
import { CustomDialogActions } from '../../../../../../shared/components/modals/CustomDialogActions'
import useMassAssignmentAddedInputs from '../../../../../../shared/hooks/useMassAssignmentAddedInputs'
import useMassAssignmentData from '../../../../../../shared/hooks/useMassAssignmentData'
import useModalState from '../../../../../../shared/hooks/useModalState'
import useTabState from '../../../../../../shared/hooks/useTabState'
import { StyledDialog } from '../../../../../../shared/styles/StyledDialog'
import { isCommercialOrClient } from '../../../../utils/RolesHelper'
import { useMassiveAssignActions } from '../../../hooks/modals/massive-assign/useMassiveAssignActions'
import { useMassiveAssignState } from '../../../hooks/modals/massive-assign/useMassiveAssignState'
import { BrandAndLinesList } from './BrandAndLinesList'
import { MenuList } from './MenuList'

function UserMassivelyAssign({ userIds }: { userIds: number[] }) {
  const { t } = useTranslation(['global'])

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { userBrands } = useSelector((state: IRootState) => state.brand)
  const { massivelyAssignData } = useSelector((state: IRootState) => state.user)

  const massEditableFields = ['languages', 'country', 'province', 'town', 'postalcode']

  const [data, setData] = useMassAssignmentData()
  const { addedInputs, buttons, addInput, removeInput, handleCheckChange } = useMassAssignmentAddedInputs(massEditableFields)
  const { currentTab, setCurrentTab, a11yProps } = useTabState()
  const { open, openModal, closeModal } = useModalState()
  const { massivelyAssign, filters, handleInputChange, setMassivelyAssignState, clearModal } = useMassiveAssignState(userIds, open)

  const { handleAssignBrand, handleAssignBrandVip, handleAssignLine, handleAssignLineVip, handleMenuChange, handleSubmit } =
    useMassiveAssignActions(userIds, massivelyAssign, setMassivelyAssignState, addedInputs, clearModal, data, filters)

  return (
    <>
      {!isCommercialOrClient(authUser.role_id) && (
        <Button id="btn-massively-assign" variant="outlined" color="secondary" onClick={() => openModal()}>
          {t('admin.people.massively-assign')}
        </Button>
      )}

      {massivelyAssignData !== null && (
        <StyledDialog open={open} onClose={closeModal} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">
            <Grid container justifyContent="space-between">
              <Typography variant="h6">{t('admin.people.massively-assign')}</Typography>
              <Button onClick={closeModal} color="primary" variant="text">
                <CloseRounded />
              </Button>
            </Grid>
          </DialogTitle>
          <DialogContent sx={theme => ({ paddingBottom: theme.spacing(3), paddingTop: theme.spacing(2) })}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <StyledTabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} aria-label="disabled tabs example">
                  <Tab label={t('general')} {...a11yProps(0)} />
                  <Tab label={t('brands')} {...a11yProps(1)} />
                  <Tab label={t('section')} {...a11yProps(2)} />
                </StyledTabs>
              </Grid>
            </Grid>
            <TabPanel value={currentTab} index={0}>
              <MassAssigmentBody
                data={data}
                setData={setData}
                addedInputs={addedInputs}
                buttons={buttons}
                addInput={addInput}
                removeInput={removeInput}
                handleCheckChange={handleCheckChange}
              />
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FamilyAutocomplete value={filters.family} handleInputChange={handleInputChange} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <BrandMultiAutocomplete value={filters.brands} handleInputChange={handleInputChange} familyId={filters.family?.id} />
                </Grid>
              </Grid>

              {filters.brands.length > 0 && (
                <Box mt={2}>
                  <div className="table_custom white">
                    <BrandAndLinesList
                      brands={userBrands.filter((brand: any) => filters.brands.includes(brand.id))}
                      massivelyAssign={massivelyAssign}
                      handleAssignBrand={handleAssignBrand}
                      handleAssignBrandVip={handleAssignBrandVip}
                      handleAssignLine={handleAssignLine}
                      handleAssignLineVip={handleAssignLineVip}
                    />
                  </div>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={currentTab} index={2}>
              <MenuList massivelyAssign={massivelyAssign} handleMenuChange={handleMenuChange} />
            </TabPanel>
          </DialogContent>
          <CustomDialogActions handleClose={closeModal} handleSubmit={handleSubmit} />
        </StyledDialog>
      )}
    </>
  )
}

export default UserMassivelyAssign
